import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Paperclip, X, Download, FileText, Image, File, Upload, Loader2 } from 'lucide-react';
import { customEntriesAPI } from '../services/api';

const ALLOWED_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'doc', 'docx', 'xls', 'xlsx', 'txt'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const getFileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType === 'application/pdf') return FileText;
  return File;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const CustomEntryAttachments = forwardRef(function CustomEntryAttachments({ 
  customEntryId, 
  attachments = [], 
  onAttachmentsChange,
  readOnly = false,
  isNew = false 
}, ref) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]); // For new custom entries
  const fileInputRef = useRef(null);
  
  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    getPendingFiles: () => pendingFiles,
    clearPendingFiles: () => setPendingFiles([]),
    uploadPendingFiles: async (entryId) => {
      const uploaded = [];
      for (const file of pendingFiles) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const response = await customEntriesAPI.uploadAttachment(entryId, formData);
          uploaded.push(response.data);
        } catch (err) {
          console.error('Failed to upload file:', file.name, err);
        }
      }
      setPendingFiles([]);
      return uploaded;
    }
  }));

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    setError(null);
    
    for (const file of files) {
      // Validate file extension
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        setError(`File type .${ext} not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
        continue;
      }
      
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }
      
      if (isNew) {
        // For new custom entries, store files to upload later
        setPendingFiles(prev => [...prev, file]);
      } else {
        // For existing custom entries, upload immediately
        await uploadFile(file);
      }
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await customEntriesAPI.uploadAttachment(customEntryId, formData);
      if (onAttachmentsChange) {
        onAttachmentsChange([...attachments, response.data]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    
    try {
      await customEntriesAPI.deleteAttachment(customEntryId, attachmentId);
      if (onAttachmentsChange) {
        onAttachmentsChange(attachments.filter(a => a.id !== attachmentId));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete attachment');
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await customEntriesAPI.downloadAttachment(customEntryId, attachment.id);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download file');
    }
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4" data-testid="custom-entry-attachments">
      {/* Upload Section */}
      {!readOnly && (
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            accept={ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(',')}
            data-testid="attachment-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
            data-testid="upload-attachment-btn"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Add Attachment
          </button>
          <span className="text-sm text-slate-500">
            Max 10MB. Allowed: PDF, Images, Word, Excel, Text
          </span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Pending Files (for new custom entries) */}
      {isNew && pendingFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-600">Files to upload:</h4>
          {pendingFiles.map((file, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <File className="w-5 h-5 text-amber-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{file.name}</p>
                <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
              </div>
              <button
                type="button"
                onClick={() => removePendingFile(index)}
                className="p-1 text-red-500 hover:bg-red-50 rounded"
                data-testid={`remove-pending-file-${index}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Attachments */}
      {attachments.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-600 flex items-center gap-2">
            <Paperclip className="w-4 h-4" />
            Attachments ({attachments.length})
          </h4>
          {attachments.map((attachment) => {
            const FileIcon = getFileIcon(attachment.content_type);
            return (
              <div key={attachment.id} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <FileIcon className="w-5 h-5 text-slate-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{attachment.original_filename}</p>
                  <p className="text-xs text-slate-500">{formatFileSize(attachment.size)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleDownload(attachment)}
                    className="p-1 text-[#194eb2] hover:bg-blue-50 rounded"
                    title="Download"
                    data-testid={`download-attachment-${attachment.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleDelete(attachment.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                      title="Delete"
                      data-testid={`delete-attachment-${attachment.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* No Attachments Message */}
      {attachments.length === 0 && pendingFiles.length === 0 && !isNew && (
        <p className="text-sm text-slate-500 italic">No attachments</p>
      )}
    </div>
  );
});

export default CustomEntryAttachments;
